import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

/**
 * Phase 14.4 — illustrative scenario snapshot for service pages.
 *
 * 3 cards (Situation → Approach → Outcome) + a small Recharts visual below
 * selected by `chartKind`. All numbers are obviously-illustrative and clearly
 * labeled; a prominent "Illustrative — not a projection or guarantee" caption
 * sits beneath the chart.
 *
 * Props:
 *   situation, approach, outcome:  strings
 *   chartKind:                     one of the keys in CHART_DATA
 *   chartTitle?:                   string title for the chart
 */

/* ---------- Illustrative chart data (compliance-safe, obviously rounded) ---------- */

const GAP_DATA = [
  { label: "Employer", Coverage: 100 },
  { label: "Needed", Coverage: 750 },
];
const INCOME_DATA = Array.from({ length: 26 }, (_, i) => ({
  age: 65 + i,
  Income: Math.round(42000 + i * 600),
}));
const CARE_COST_DATA = [
  { age: 65, Cost: 0 },
  { age: 70, Cost: 15 },
  { age: 75, Cost: 55 },
  { age: 80, Cost: 95 },
  { age: 85, Cost: 150 },
  { age: 90, Cost: 180 },
];
const COLLEGE_DATA = Array.from({ length: 16 }, (_, i) => ({
  year: i,
  Balance: Math.round(600 * ((1.06 ** i - 1) / 0.06) * 12 / 1000),
}));
const INCOME_REPLACE_DATA = [
  { label: "No DI", Paycheck: 0 },
  { label: "With DI", Paycheck: 60 },
];
const KEY_MAN_DATA = [
  { label: "Year 1 after loss", Revenue: 60 },
  { label: "Year 2", Revenue: 75 },
  { label: "Year 3 (with buy-sell)", Revenue: 95 },
];
const ESTATE_DATA = [
  { label: "Without plan", "To heirs": 65, Friction: 35 },
  { label: "With plan", "To heirs": 92, Friction: 8 },
];
const WEALTH_CURVE_DATA = Array.from({ length: 50 }, (_, i) => {
  const age = 25 + i;
  // Accumulation to 65, distribution tapering thereafter
  const balance =
    age < 65
      ? Math.round(50 * ((1.07 ** (age - 25) - 1) / 0.07))
      : Math.round(50 * ((1.07 ** 40 - 1) / 0.07) * Math.max(0.1, 1 - (age - 65) * 0.03));
  return { age, Balance: balance };
});

const CHART_RENDERERS = {
  gap: (data = GAP_DATA) => (
    <BarChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B7280" }} />
      <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={(v) => `$${v}k`} />
      <Tooltip formatter={(v) => `$${v}k`} contentStyle={{ fontSize: 12 }} />
      <Bar dataKey="Coverage" fill="#D4A24C" radius={[6, 6, 0, 0]} />
    </BarChart>
  ),
  income: (data = INCOME_DATA) => (
    <AreaChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 10 }}>
      <defs>
        <linearGradient id="incomeFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#D4A24C" stopOpacity={0.5} />
          <stop offset="100%" stopColor="#D4A24C" stopOpacity={0} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
      <XAxis dataKey="age" tick={{ fontSize: 11, fill: "#6B7280" }} />
      <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
      <Tooltip formatter={(v) => `$${v.toLocaleString()}`} contentStyle={{ fontSize: 12 }} />
      <Area type="monotone" dataKey="Income" stroke="#0B2545" fill="url(#incomeFill)" strokeWidth={2} />
    </AreaChart>
  ),
  careCost: (data = CARE_COST_DATA) => (
    <LineChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 10 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
      <XAxis dataKey="age" tick={{ fontSize: 11, fill: "#6B7280" }} />
      <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={(v) => `$${v}k`} />
      <Tooltip formatter={(v) => `$${v}k / yr`} contentStyle={{ fontSize: 12 }} />
      <Line type="monotone" dataKey="Cost" stroke="#D4A24C" strokeWidth={2.5} dot={{ r: 4, fill: "#D4A24C" }} />
    </LineChart>
  ),
  incomeReplace: (data = INCOME_REPLACE_DATA) => (
    <BarChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B7280" }} />
      <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={(v) => `${v}%`} />
      <Tooltip formatter={(v) => `${v}% of base`} contentStyle={{ fontSize: 12 }} />
      <Bar dataKey="Paycheck" fill="#0B2545" radius={[6, 6, 0, 0]} />
    </BarChart>
  ),
  keymanLoss: (data = KEY_MAN_DATA) => (
    <BarChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
      <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6B7280" }} />
      <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={(v) => `${v}%`} />
      <Tooltip formatter={(v) => `${v}% of baseline`} contentStyle={{ fontSize: 12 }} />
      <Bar dataKey="Revenue" fill="#D4A24C" radius={[6, 6, 0, 0]} />
    </BarChart>
  ),
  college529: (data = COLLEGE_DATA) => (
    <AreaChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 10 }}>
      <defs>
        <linearGradient id="collegeFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#D4A24C" stopOpacity={0.45} />
          <stop offset="100%" stopColor="#D4A24C" stopOpacity={0} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
      <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#6B7280" }} />
      <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={(v) => `$${v}k`} />
      <Tooltip formatter={(v) => `$${v}k`} contentStyle={{ fontSize: 12 }} />
      <Area type="monotone" dataKey="Balance" stroke="#0B2545" fill="url(#collegeFill)" strokeWidth={2} />
    </AreaChart>
  ),
  estateTransfer: (data = ESTATE_DATA) => (
    <BarChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B7280" }} />
      <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={(v) => `${v}%`} />
      <Tooltip contentStyle={{ fontSize: 12 }} />
      <Bar dataKey="To heirs" stackId="a" fill="#D4A24C" radius={[6, 6, 0, 0]} />
      <Bar dataKey="Friction" stackId="a" fill="#6B7280" />
    </BarChart>
  ),
  wealthCurve: (data = WEALTH_CURVE_DATA) => (
    <AreaChart data={data} margin={{ top: 10, right: 20, bottom: 0, left: 10 }}>
      <defs>
        <linearGradient id="wealthFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0B2545" stopOpacity={0.4} />
          <stop offset="100%" stopColor="#0B2545" stopOpacity={0} />
        </linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
      <XAxis dataKey="age" tick={{ fontSize: 11, fill: "#6B7280" }} />
      <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={(v) => `$${v}k`} />
      <Tooltip formatter={(v) => `$${v}k`} contentStyle={{ fontSize: 12 }} />
      <Area type="monotone" dataKey="Balance" stroke="#D4A24C" fill="url(#wealthFill)" strokeWidth={2.5} />
    </AreaChart>
  ),
};

function ScenarioCard({ eyebrow, body, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex-1 rounded-2xl border border-lw-navy/10 bg-white p-6 min-h-[164px]"
    >
      <span className="lw-eyebrow" style={{ marginBottom: 6 }}>
        {eyebrow}
      </span>
      <p className="mt-3 text-sm text-lw-navy/90 leading-relaxed">{body}</p>
    </div>
  );
}

export const ScenarioSnapshot = ({
  situation,
  approach,
  outcome,
  chartKind,
  chartTitle,
}) => {
  if (!situation || !approach || !outcome) return null;
  const render = CHART_RENDERERS[chartKind];

  return (
    <div data-testid="scenario-snapshot" className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ScenarioCard eyebrow="Situation" body={situation} testid="scenario-situation" />
        <ScenarioCard eyebrow="Approach" body={approach} testid="scenario-approach" />
        <ScenarioCard eyebrow="Outcome" body={outcome} testid="scenario-outcome" />
      </div>

      {render && (
        <div className="mt-6 rounded-2xl border border-lw-navy/10 bg-lw-ivory-warm/60 p-5 sm:p-6">
          {chartTitle && (
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-lw-navy mb-3">
              {chartTitle}
            </p>
          )}
          <div style={{ width: "100%", height: 220 }} data-testid={`scenario-chart-${chartKind}`}>
            <ResponsiveContainer width="100%" height="100%">
              {render()}
            </ResponsiveContainer>
          </div>
          <p className="mt-3 text-[0.72rem] leading-relaxed text-lw-muted italic">
            Illustrative — not a projection or guarantee. Actual results depend on
            carrier, product, underwriting, markets, and the specifics of your
            situation. See full disclosures below.
          </p>
        </div>
      )}
    </div>
  );
};

export default ScenarioSnapshot;
